import { _ } from "libs";

import React, { ReactElement, useState } from "react";
import DropdownGroup from "./dropdownFilter/dropdownGroup";
import DropdownSlider from "./dropdownFilter/dropdownSlider";
import DropdownOption from "./dropdownFilter/dropdownOption";
import DropdownDefaultRadioButton from "./dropdownFilter/dropdownDefaultRadioButton";
import MultiSearchInput from "./multiSearchInput";
import { MultiSearchResult } from "../types";
import DropdownSearch from "./dropdownFilter/dropdownSearch";

const defaultAge: number[] | null[] = [0, 100];
const defaultGender = "any";
const defaultSkill = "any";
const defaultLocation: MultiSearchResult = null;
export const defaultFilter = {
	age: defaultAge,
	gender: defaultGender,
	skill: defaultSkill,
	location: defaultLocation,
};
export enum PlayersFilterType {
	AGE = "age",
	GENDER = "gender",
	SKILL = "skill",
	LOCATION = "location",
}
export type PlayersFilter = typeof defaultFilter;
type OnFilterChange = (filter: PlayersFilter) => void;

const PlayerFilter = ({
	onFilterChange,
	value,
	types = [PlayersFilterType.AGE, PlayersFilterType.GENDER, PlayersFilterType.SKILL],
}: {
	onFilterChange: OnFilterChange;
	value?: PlayersFilter;
	types?: PlayersFilterType[];
}) => {
	const [filter, changeFilter] = useState(value || defaultFilter);

	const onSingleFilterChange = (name: string, value: string | number) => {
		const newFilter = { ...filter, [name]: value };
		changeFilter(newFilter);
		onFilterChange(newFilter);
	};

	const onAgeFilterChange = (name: string, value: number[]) => {
		const newFilter = { ...filter, [name]: value };
		changeFilter(newFilter);
		onFilterChange(newFilter);
	};

	const onLocationChange = (suggestion: MultiSearchResult | string) => {
		if (suggestion instanceof Object) {
			const newFilter = { ...filter, location: suggestion };
			changeFilter(newFilter);
			onFilterChange(newFilter);
		}
	};

	const getFilterType = (type: PlayersFilterType, index: number): ReactElement => {
		let res;
		switch (type) {
			case PlayersFilterType.AGE: {
				res = (
					<DropdownSlider
						name="age"
						title={_("playerFilter.age.title")}
						placeholder={_("playerFilter.age.placeholder")}
						emptyValue={defaultAge}
						onChange={onAgeFilterChange}
						value={filter.age}
						key="filter-age"
					/>
				);
				break;
			}
			case PlayersFilterType.GENDER: {
				res = (
					<DropdownDefaultRadioButton
						name="gender"
						title={_("playerFilter.gender.title")}
						placeholder={_("playerFilter.gender.placeholder")}
						emptyValue={defaultGender}
						onChange={onSingleFilterChange}
						value={filter.gender}
						key="filter-gender"
					>
						<DropdownOption title={_("gender.any")} value={"any"} />
						<DropdownOption title={_("gender.male")} value={"M"} />
						<DropdownOption title={_("gender.female")} value={"F"} />
					</DropdownDefaultRadioButton>
				);
				break;
			}
			case PlayersFilterType.SKILL: {
				res = (
					<DropdownDefaultRadioButton
						name="skill"
						title={_("playerFilter.skill.title")}
						placeholder={_("playerFilter.skill.placeholder")}
						emptyValue={defaultSkill}
						onChange={onSingleFilterChange}
						value={filter.skill}
						key="filter-skill"
					>
						<DropdownOption title={_("skill.any")} value={"any"} />
						<DropdownOption title={_("skill.better")} value={"better"} />
						<DropdownOption title={_("skill.similar")} value={"similar"} />
						<DropdownOption title={_("skill.coach")} value={"coach"} />
					</DropdownDefaultRadioButton>
				);
				break;
			}
			case PlayersFilterType.LOCATION: {
				res = (
					<DropdownSearch
						title={_("playerFilter.location.placeholder")}
						placeholder={_("playerFilter.location.placeholder")}
						value={filter.location?.value}
						key="filter-location"
					>
						<MultiSearchInput
							onChange={onLocationChange}
							value={filter.location?.value}
							valueProperty={"location"}
							getDisplayValue={undefined}
							placeholder={_("form.city.label")}
							types={["place"]}
						/>
					</DropdownSearch>
				);
				break;
			}
		}
		return res;
	};

	const getFilterTypes = (types: PlayersFilterType[]): ReactElement[] => {
		return types.map((type, index) => getFilterType(type, index));
	};

	return (
		<div className="filter playersFilter">
			<DropdownGroup>{getFilterTypes(types)}</DropdownGroup>
		</div>
	);
};

export default PlayerFilter;
